import isEmpty from "lodash/isEmpty"
import get from "lodash/get"
import { useState, useEffect, useRef } from "react"
import { handleEnterPress } from "utils-lib/handleEnterPress"
import {
  getAdobeLaunchAttribute,
  getAdobeLaunchClassName,
} from "utils-lib/analytics/launch"
import { updateClicksDL } from "utils-lib/analytics/dataLayer"
import { isPdfUrl } from "utils-lib/validation/validateHashTagUrl"
import "./Link.scss"
import { useRouter } from "next/router"
import { isNextApp } from "utils-lib/isNextApp"
import { Link as ReactRouterLink } from "react-router-dom"
import useFeatureFlags from "hooks-lib/LaunchDarkly/useFeatureFlags"
import getIsRouteNextJsEnabled from "utils-lib/nextJs/getIsRouteNextJsEnabled"
import isLocalRouteValidationEnabled from "utils-lib/nextJs/isLocalRouteValidationEnabled"
import { getWindow } from "utils-lib/getWindow"

const ssrWindow = getWindow()

export const Link = ({
  style = {},
  onClick = () => {},
  onKeyDown = () => {},
  href = ``,
  isExternalLink = false,
  isDownloadLink = false,
  isOpenInNewTab = false,
  children = "",
  className = ``,
  overrideClass = ``,
  backgroundClass = `bg-transparent`,
  analytics_key = ``,
  shouldNotTrackInComponent = false,
  disabled = false,
  tabIndex = `0`,
  ariaLabel = ``,
  ariaDescription = ``,
  useWhiteLink = false,
  useUnderlineHover = false,
  isSCA = false,
  scaValues = {},
  removeUpperCase = false,
  setSCAValues,
  forceHideUnderline = false,
  elementType = `Link`,
  target = "_self",
  testid = "Link",
  ...rest
}) => {
  const [showUnderline, setShowUnderline] = useState(false)
  const [cleanedHref] = useState(href?.replace("/content/wm", ""))
  const nextLinkRef = useRef(null)
  const nextRouterRef = useRef(null)
  const isNextJsEnabledRef = useRef(false)

  const restProps = {
    ...rest,
    removeUpperCase,
  }

  const { LDFFRouteEnhancedNextJsRouting } = useFeatureFlags([
    "LDFFRouteEnhancedNextJsRouting",
  ])

  // we need to use a ref to store the value of the feature flag so that we can use it in the event listeners
  useEffect(() => {
    if (LDFFRouteEnhancedNextJsRouting && cleanedHref) {
      const isNextJsEnabled = getIsRouteNextJsEnabled(
        cleanedHref,
        LDFFRouteEnhancedNextJsRouting,
      )

      isNextJsEnabledRef.current = isNextJsEnabled
    }
  }, [LDFFRouteEnhancedNextJsRouting, cleanedHref])

  useEffect(() => {
    const nextLink = nextLinkRef.current
    if (nextLink) {
      nextLink.addEventListener("click", handleNavigation)
      nextLink.addEventListener("keydown", handleNextKeyDown)
      nextLink.addEventListener("mouseenter", handleHover)
      nextLink.addEventListener("focus", handleHover)
    }
  }, [nextLinkRef])

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    // cleanup event listeners
    return () => {
      const nextLink = nextLinkRef.current

      if (nextLink) {
        nextLink.removeEventListener("click", handleNavigation)
        nextLink.removeEventListener("keydown", handleNextKeyDown)
        nextLink.removeEventListener("mouseenter", handleHover)
        nextLink.removeEventListener("focus", handleHover)
      }
    }
  }, [])

  let clickLabel = ``
  if (typeof analytics_key === `string` && analytics_key !== ``) {
    clickLabel = analytics_key
  } else if (typeof children === `string`) {
    clickLabel = children
  } else {
    const child = get(children, `[0]`, ``)
    if (typeof child === `string`) {
      clickLabel = child
    }
  }

  const analyticsClass = getAdobeLaunchClassName({
    disabled: shouldNotTrackInComponent,
    isSCA,
  })

  const linkProps = {
    "data-testid": testid,
    className: overrideClass
      ? `${overrideClass} ${analyticsClass} ${backgroundClass}`
      : `Link ${analyticsClass} btn ${
          useWhiteLink ? `white-link` : `wm-link`
        } ${backgroundClass} ${className} ${
          useUnderlineHover ? `underline-hover` : ``
        }`,
    style: {
      ...style,
      ...{ decoration: forceHideUnderline ? `none !important` : `` },
    },
    disabled,
    tabIndex,
    ...(useUnderlineHover
      ? {
          onMouseOver: () => {
            setShowUnderline(true)
          },
          onMouseOut: () => {
            setShowUnderline(false)
          },
        }
      : {}),
    ...getAdobeLaunchAttribute({
      disabled: shouldNotTrackInComponent,
      value: clickLabel,
    }),
    onKeyUp: (e) => {
      handleEnterPress(e, () => {
        if (!shouldNotTrackInComponent) {
          updateClicksDL({
            ui_element: elementType,
            object_content: clickLabel,
          })

          if (isSCA) {
            setSCAValues(scaValues)
          }
        }
        onClick(e)
      })
    },
    onClick: (e) => {
      /* Tag the link click. */
      if (!shouldNotTrackInComponent) {
        updateClicksDL({
          ui_element: elementType,
          object_name: ``,
          object_content: clickLabel,
        })

        if (isSCA) {
          setSCAValues(scaValues)
        }
      }
      onClick(e)
    },
    ...(ariaLabel !== `` ? { "aria-label": ariaLabel } : {}),
    ...(ariaDescription !== `` ? { "aria-description": ariaDescription } : {}),
    ...restProps,
  }

  async function handleNextKeyDown(e) {
    handleEnterPress(e, () => {
      handleNavigation(e)
      linkProps.onKeyUp()
    })
  }

  async function handleNavigation(e) {
    // Prevent the event from bubbling up to parent components.
    // This ensures the click event is handled manually,
    // avoiding duplicate entries in the history stack caused by the parent component also handling the click event.
    e.stopPropagation()

    const router = nextRouterRef.current
    const hasHash = href?.indexOf("#") !== -1
    const isHashLink = cleanedHref?.indexOf("#") === 0

    const isNextJsEnabled = isNextJsEnabledRef.current

    // If the target is `_blank`, we don't want to handle the navigation, as it will be handled by the browser and open a new tab.
    // isNextJsEnabled logic is not an issue here as it will be a hard reload anyway
    // using a ref here to properly handle feature flag changes in the event listener
    if (target === `_blank`) return

    if (!isHashLink && !isNextJsEnabled) {
      // trigger a hard reload if the link is not next js enabled
      ssrWindow.location.pathname = cleanedHref

      // skip the rest of the checks to prevent duplicate entries in the history stack
      return
    }

    if (!hasHash) {
      e.preventDefault()
      await router.push(cleanedHref)

      // handles closing the meganav
      linkProps.onClick()
    } else {
      await router.push(cleanedHref)
    }
  }

  async function handleHover() {
    const isLocal = process.env.NEXT_PUBLIC_IS_LOCAL_ENV
    const router = nextRouterRef.current

    const isNextJsEnabled = isNextJsEnabledRef.current

    if (isLocal && !isLocalRouteValidationEnabled()) {
      return
    }

    // only prefetch if the route is next js enabled, this prevents us from wasting serverless execution time on non next js routes
    if (isNextJsEnabled) {
      router.prefetch(cleanedHref)
    }
  }

  // This allows us to support next js apps without stuffing react router into them
  const nextCompatLinkProps = {
    ...linkProps,
    ...getAdobeLaunchAttribute({
      disabled: shouldNotTrackInComponent,
      value: clickLabel,
    }),
    onKeyDown,
    target: target !== `_self` ? `_blank` : target,
  }

  let linkComponent = null
  if (isNextApp && !isEmpty(href)) {
    if (!nextRouterRef.current) {
      // only initialize the next router once and when the component is in a next app and the isExternalLink is false, these changes are needed as the singleton router implemntation was broken after the next 15 upgrade
      nextRouterRef.current = useRouter()
    }

    if (!isExternalLink) {
      linkComponent = (
        <a href={cleanedHref} ref={nextLinkRef} {...nextCompatLinkProps}>
          {children}
        </a>
      )
    } else {
      linkComponent = (
        <a href={cleanedHref} {...nextCompatLinkProps}>
          {children}
        </a>
      )
    }
  } else {
    nextCompatLinkProps.to = href

    linkComponent = href ? (
      isExternalLink === false ? (
        <ReactRouterLink {...nextCompatLinkProps}>{children}</ReactRouterLink>
      ) : (
        <a
          {...linkProps}
          download={isDownloadLink}
          href={href}
          rel="noopener noreferrer"
          target={isPdfUrl(href) || target !== `_self` ? `_blank` : target}
          {...(isOpenInNewTab && {
            target: `_blank`,
            rel: "noopener noreferrer",
          })}
        >
          {children}
        </a>
      )
    ) : (
      <button {...linkProps} type="button">
        {children}
      </button>
    )
  }

  return useUnderlineHover ? (
    <div>
      {linkComponent}
      {showUnderline && <div className="LinkHoverUnderline" />}
    </div>
  ) : (
    linkComponent
  )
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// Link.propTypes = {
//   style: PropTypes.object,
//   onClick: PropTypes.func,
//   onKeyDown: PropTypes.func,
//   href: PropTypes.string,
//   isExternalLink: PropTypes.bool,
//   isDownloadLink: PropTypes.bool,
//   isOpenInNewTab: PropTypes.bool,
//   children: PropTypes.node,
//   className: PropTypes.string,
//   overrideClass: PropTypes.string,
//   backgroundClass: PropTypes.string,
//   analytics_key: PropTypes.string,
//   shouldNotTrackInComponent: PropTypes.bool,
//   disabled: PropTypes.bool,
//   tabIndex: PropTypes.string,
//   ariaLabel: PropTypes.string,
//   ariaDescription: PropTypes.string,
//   useWhiteLink: PropTypes.bool,
//   useUnderlineHover: PropTypes.bool,
//   isSCA: PropTypes.bool,
//   scaValues: PropTypes.object,
//   removeUpperCase: PropTypes.bool,
//   setSCAValues: PropTypes.func.isRequired,
//   forceHideUnderline: PropTypes.bool,
//   elementType: PropTypes.string,
//   target: PropTypes.string,
//   testid: PropTypes.string,
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// Link.defaultProps = {
//   children: "",
//   style: {},
//   onClick: () => {},
//   onKeyDown: () => {},
//   href: ``,
//   isExternalLink: false,
//   isDownloadLink: false,
//   isOpenInNewTab: false,
//   className: ``,
//   overrideClass: ``,
//   backgroundClass: `bg-transparent`,
//   analytics_key: ``,
//   shouldNotTrackInComponent: false,
//   disabled: false,
//   tabIndex: `0`,
//   ariaLabel: ``,
//   ariaDescription: ``,
//   useWhiteLink: false,
//   useUnderlineHover: false,
//   isSCA: false,
//   scaValues: {},
//   removeUpperCase: false,
//   elementType: `Link`,
//   target: "_self",
//   testid: "Link",
//   forceHideUnderline: false,
// }

export default Link
